(function () {
  const STORAGE_KEY = 'ms:wishlist';
  const CHANGE_EVENT = 'ms:wishlist:change';

  function readList() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (_) {}

    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.themeVariables?.settings?.moneyFormat);
    }

    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    });
  }

  function getItemHandle(item) {
    if (item?.handle) return item.handle;

    const match = String(item?.url || '').match(/\/products\/([^/?#]+)/i);
    return match ? match[1] : '';
  }

  function normalizeImage(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || '';
  }

  window.MsWishlist = {
    storageKey: STORAGE_KEY,
    changeEvent: CHANGE_EVENT,
    readList,
    writeList,
    count() {
      return readList().length;
    },
    has(productId) {
      return readList().some((item) => String(item.productId) === String(productId));
    },
    remove(productId) {
      const list = readList().filter((item) => String(item.productId) !== String(productId));
      writeList(list);
      return list;
    },
    toggle(item) {
      const list = readList();
      const index = list.findIndex((entry) => String(entry.productId) === String(item.productId));
      const payload = {
        productId: String(item.productId),
        variantId: String(item.variantId || ''),
        handle: getItemHandle(item),
        title: item.title || '',
        url: item.url || '',
        image: normalizeImage(item.image)
      };

      if (index >= 0) {
        list.splice(index, 1);
      } else {
        list.unshift(payload);
      }

      writeList(list);
      return list;
    }
  };

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.update = this.update.bind(this);
      this.update();
      document.addEventListener(CHANGE_EVENT, this.update);
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.update);
    }

    update() {
      const count = window.MsWishlist.count();
      this.textContent = String(count);
      this.toggleAttribute('hidden', count === 0);
      this.closest('.header__wishlist-link')?.classList.toggle('has-items', count > 0);
    }
  }

  class MsProductWishlist extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.ms-product-wishlist__button');
      this.label = this.querySelector('.ms-product-wishlist__label');
      this.productId = String(this.dataset.productId || '');
      this.onChange = this.syncState.bind(this);

      this.syncState();
      this.button?.addEventListener('click', () => this.toggle());
      document.addEventListener(CHANGE_EVENT, this.onChange);
      document.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;
        if (!variant || String(variant.product_id) !== this.productId) return;

        this.dataset.variantId = String(variant.id);
        if (this.button) {
          this.button.dataset.variantId = String(variant.id);
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    buildItem() {
      return {
        productId: this.productId,
        variantId: this.button?.dataset.variantId || this.dataset.variantId || '',
        handle: this.button?.dataset.productHandle || '',
        title: this.button?.dataset.productTitle || '',
        url: this.button?.dataset.productUrl || '',
        image: this.button?.dataset.productImage || ''
      };
    }

    syncState() {
      const saved = window.MsWishlist.has(this.productId);
      this.button?.classList.toggle('is-active', saved);
      this.button?.setAttribute('aria-pressed', saved ? 'true' : 'false');

      if (this.label) {
        this.label.textContent = saved ? 'Saved to Wishlist' : 'Add to Wishlist';
      }
    }

    toggle() {
      if (window.SwymProductBtn?.click) {
        this.button?.click();
        return;
      }

      window.MsWishlist.toggle(this.buildItem());
      this.syncState();
    }
  }

  class MsWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.ms-wishlist-page__grid');
      this.emptyState = this.querySelector('.ms-wishlist-page__empty');
      this.loadingState = this.querySelector('.ms-wishlist-page__loading');
      this.render = this.render.bind(this);

      this.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-remove-wishlist]');
        if (removeButton) {
          event.preventDefault();
          window.MsWishlist.remove(removeButton.dataset.removeWishlist);
          return;
        }

        const addButton = event.target.closest('[data-add-to-cart]');
        if (addButton) {
          event.preventDefault();
          this.addToCart(addButton);
        }
      });

      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    async addToCart(button) {
      const variantId = button.dataset.addToCart;
      if (!variantId) return;

      button.setAttribute('disabled', 'disabled');

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        });

        if (!response.ok) throw new Error('Unable to add item');

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        button.textContent = button.dataset.addedLabel || 'Added';
      } catch (_) {
        button.textContent = button.dataset.errorLabel || 'Try again';
      } finally {
        window.setTimeout(() => {
          button.removeAttribute('disabled');
          button.textContent = button.dataset.defaultLabel || 'Add to bag';
        }, 1800);
      }
    }

    async fetchProduct(item) {
      const handle = getItemHandle(item);
      if (!handle) return null;

      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    }

    buildCard(item, product) {
      const handle = getItemHandle(item);
      const variant = product?.variants?.find((entry) => String(entry.id) === String(item.variantId)) || product?.variants?.[0];
      const image = normalizeImage(variant?.featured_image) || normalizeImage(product?.featured_image) || normalizeImage(item.image);
      const title = product?.title || item.title || 'Product';
      const url = product?.url || item.url || (handle ? `/products/${handle}` : '#');
      const price = variant?.price ?? product?.price;
      const compareAt = variant?.compare_at_price ?? product?.compare_at_price;
      const variantId = variant?.id || item.variantId;
      const canPurchase = Boolean(product && variantId && (variant?.available ?? product?.available));

      return `
        <article class="ms-wishlist-card" data-product-id="${item.productId}">
          <a href="${url}" class="ms-wishlist-card__media">
            ${image ? `<img src="${image}" alt="${title.replace(/"/g, '&quot;')}" loading="lazy" width="480" height="480">` : ''}
          </a>
          <div class="ms-wishlist-card__body">
            <a href="${url}" class="ms-wishlist-card__title">${title}</a>
            <div class="ms-wishlist-card__price">
              ${compareAt && price != null && compareAt > price ? `<span class="ms-wishlist-card__compare">${formatMoney(compareAt)}</span>` : ''}
              <span>${price != null ? formatMoney(price) : ''}</span>
            </div>
            <div class="ms-wishlist-card__actions">
              ${
                canPurchase
                  ? `<button type="button" class="button button--outline ms-wishlist-card__add" data-add-to-cart="${variantId}" data-default-label="Add to bag" data-added-label="Added" data-error-label="Try again">Add to bag</button>`
                  : `<a href="${url}" class="ms-wishlist-card__view link">View product</a>`
              }
              <button type="button" class="ms-wishlist-card__remove" data-remove-wishlist="${item.productId}">Remove</button>
            </div>
          </div>
        </article>
      `;
    }

    async render() {
      const list = window.MsWishlist.readList();

      if (!this.grid || !this.emptyState) return;

      if (list.length === 0) {
        this.grid.hidden = true;
        this.grid.innerHTML = '';
        this.emptyState.hidden = false;
        if (this.loadingState) this.loadingState.hidden = true;
        return;
      }

      this.emptyState.hidden = true;
      if (this.loadingState) this.loadingState.hidden = false;
      this.grid.hidden = true;

      const cards = await Promise.all(
        list.map(async (item) => {
          const product = await this.fetchProduct(item);
          return this.buildCard(item, product);
        })
      );

      this.grid.innerHTML = cards.join('');
      this.grid.hidden = false;
      if (this.loadingState) this.loadingState.hidden = true;
    }
  }

  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', WishlistCount);
  }

  if (!customElements.get('ms-product-wishlist')) {
    customElements.define('ms-product-wishlist', MsProductWishlist);
  }

  if (!customElements.get('ms-wishlist-page')) {
    customElements.define('ms-wishlist-page', MsWishlistPage);
  }

  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list: readList() } }));
})();
